/**
 * CaseSummaryHeader — compact status overview at the top of the investigation timeline.
 *
 * Shows badge counts per status category and a one-line assessment message.
 */

import { Shield } from 'lucide-react';
import type { InvestigationSummary } from '../../types/orderInvestigation';
import { Badge, type BadgeTone } from '../ui/badge';
import type { ProvenanceTier } from '../../config/colors';

interface CaseSummaryHeaderProps {
  summary: InvestigationSummary;
}

// Tier badge colours come from the shared colors module via `Badge`'s
// `provenance` prop — inferred is violet. `missing` and `contradictory`
// are not provenance tiers and take the nearest tone.
const STATUS_BADGES: {
  key: keyof Omit<InvestigationSummary, 'message' | 'overallCaseStatus'>;
  label: string;
  badge: { tone?: BadgeTone; provenance?: ProvenanceTier };
}[] = [
  { key: 'observed',      label: 'Observed',      badge: { provenance: 'observed' } },
  { key: 'declared',      label: 'Declared',      badge: { provenance: 'declared' } },
  { key: 'inferred',      label: 'Inferred',      badge: { provenance: 'inferred' } },
  { key: 'missing',       label: 'Missing',       badge: { tone: 'neutral' } },
  { key: 'contradictory', label: 'Contradictory', badge: { tone: 'destructive' } },
];

export default function CaseSummaryHeader({ summary }: CaseSummaryHeaderProps) {
  return (
    <div className="bg-secondary/30 rounded-lg p-4 border border-accent/30">
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-4 h-4 text-brand" />
        <span className="text-sm font-semibold text-foreground uppercase tracking-wide">
          Evidence Summary
        </span>
      </div>

      {/* Badge row */}
      <div className="flex flex-wrap gap-2 mb-3">
        {STATUS_BADGES.map(({ key, label, badge }) => {
          const count = summary[key];
          if (count === 0) return null;
          return (
            <Badge key={key} className="gap-1.5 px-2.5 py-1" {...badge}>
              <span className="font-bold">{count}</span>
              {label}
            </Badge>
          );
        })}
      </div>

      {/* Assessment message */}
      <p className="text-sm text-muted-foreground leading-relaxed">{summary.message}</p>
    </div>
  );
}
